<template>
  <div
    class="project-access-selector"
    :class="{ 'project-access-selector--disabled': disabled }"
  >
    <b-form-input
      v-model="search"
      placeholder="Search projects"
      debounce="200"
      size="sm"
      class="mb-2"
      :disabled="disabled"
    />

    <div v-if="isLoading" class="text-center py-3">
      <b-spinner small variant="primary" />
    </div>

    <template v-else>
      <div
        v-if="projects.length === 0"
        class="text-muted text-center py-3 small"
      >
        No projects available.
      </div>

      <template v-else>
        <div class="d-flex justify-content-between align-items-center mb-1">
          <small class="text-muted"> {{ selectedIds.length }} selected </small>
          <div>
            <b-button
              variant="link"
              size="sm"
              class="p-0 mr-3"
              :disabled="disabled"
              @click="selectAll"
            >
              Select all
            </b-button>
            <b-button
              variant="link"
              size="sm"
              class="p-0"
              :disabled="disabled"
              @click="clearAll"
            >
              Clear
            </b-button>
          </div>
        </div>

        <div class="project-list border rounded p-2">
          <div
            v-if="filteredProjects.length === 0"
            class="text-muted small text-center py-2"
          >
            No projects match "{{ search }}".
          </div>
          <b-form-checkbox
            v-for="project in filteredProjects"
            :key="project.id"
            :checked="selectedIds.includes(project.id)"
            :disabled="disabled"
            class="py-1"
            @change="toggle(project.id, $event)"
          >
            {{ project.name }}
          </b-form-checkbox>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
export default {
  name: 'ProjectAccessSelector',

  props: {
    // v-model — array of selected project ids
    value: {
      type: Array,
      default: () => [],
    },
    // Greys the whole control out and blocks interaction. Consumers that have
    // a scope toggle above the list (e.g. "applies to all projects") use this
    // rather than unmounting the selector, so the picked ids survive a toggle.
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      projects: [],
      search: '',
      isLoading: false,
    }
  },

  computed: {
    selectedIds() {
      return Array.isArray(this.value) ? this.value : []
    },

    filteredProjects() {
      const term = this.search.trim().toLowerCase()
      if (!term) return this.projects
      return this.projects.filter(
        (p) => p.name && p.name.toLowerCase().includes(term),
      )
    },
  },

  created() {
    this.loadProjects()
  },

  methods: {
    async loadProjects() {
      this.isLoading = true
      try {
        // Pull a large page of active projects so the picker has the full list
        // without pagination. Supervisors are only assignable to active projects.
        const { data } = await this.$api.get('projects', {
          params: { limit: 500, page: 1, 'filter[is_archived]': 0 },
        })
        const items = (data && data.data) || []
        this.projects = items.map((p) => ({ id: p.id, name: p.name }))
      } catch (err) {
        this.projects = []
      } finally {
        this.isLoading = false
      }
    },

    toggle(projectId, checked) {
      const next = this.selectedIds.filter((id) => id !== projectId)
      if (checked) next.push(projectId)
      this.$emit('input', next)
    },

    selectAll() {
      this.$emit(
        'input',
        this.projects.map((p) => p.id),
      )
    },

    clearAll() {
      this.$emit('input', [])
    },
  },
}
</script>

<style scoped>
.project-list {
  max-height: 240px;
  overflow-y: auto;
}

.project-access-selector--disabled {
  opacity: 0.55;
  pointer-events: none;
}
</style>
